import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import {
  useLazyGetSpecificMultiLevelFieldsQuery,
  useMultiLevelFieldsMutation,
  useDeleteMultiLevelFieldsMutation,
  useDeleteMultiLevelFieldsLabelMutation,
  useUpdateMultiLevelDynamicMutation,
  useUpdateLabelMultiLevelDynamicMutation,
} from "../../../../Services/settingsAPIs/CommonAPI";
import AddChoiceModal from "./AddChoiceModal";
import EditChoiceModal from "./EditChoiceModal";
import EditLabelModal from "./EditLabelModal";
import AddSubLabelModal from "./AddSubLabelModal";
import {
  getBucketKey,
  LevelBucketMap,
  LevelBucket,
  LevelItem,
  removeNodeDescendantBuckets,
  setBucketData,
  setBucketLoading,
} from "../../../../utils/lazyMultiLevelConfiguratorState";

interface Props {
  fieldData: any;
}

const MAX_LEVEL = 4;

const deleteCustomModalStyles: ReactModal.Styles = {
  content: {
    top: "50%",
    left: "50%",
    width: "30%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

const LazyNestedFieldConfigurator: React.FC<Props> = ({ fieldData }) => {
  const [fetchLevelOptions] = useLazyGetSpecificMultiLevelFieldsQuery();
  const [addMultiLevelChoice, { isLoading: isAdding }] = useMultiLevelFieldsMutation();
  const [deleteMultiLevelFields] = useDeleteMultiLevelFieldsMutation();
  const [deleteMultiLevelFieldsLabel] = useDeleteMultiLevelFieldsLabelMutation();
  const [updateMultiLevelDynamic, { isLoading: isUpdating }] =
    useUpdateMultiLevelDynamicMutation();
  const [updateLabelMultiLevelDynamic, { isLoading: isUpdatingLabel }] =
    useUpdateLabelMultiLevelDynamicMutation();

  const [buckets, setBuckets] = useState<LevelBucketMap>({});
  const [expandedLevels, setExpandedLevels] = useState<string[]>([]);
  const [expandedChoices, setExpandedChoices] = useState<number[]>([]);

  const [showAddChoiceModal, setShowAddChoiceModal] = useState(false);
  const [addChoiceName, setAddChoiceName] = useState("");
  const [addChoiceParentId, setAddChoiceParentId] = useState<number | string | null>(null);
  const [addChoiceLevel, setAddChoiceLevel] = useState(1);
  const [addChoiceFieldLabel, setAddChoiceFieldLabel] = useState("");
  const [addChoiceError, setAddChoiceError] = useState("");

  const [editModalState, setEditModalState] = useState<{
    show: boolean;
    value: string;
    error: string;
    item?: LevelItem;
    parentId?: number | string;
    level?: number;
  }>({ show: false, value: "", error: "" });

  const [editLabelModalState, setEditLabelModalState] = useState<{
    show: boolean;
    value: string;
    error: string;
    parentId?: number | string;
    level?: number;
  }>({ show: false, value: "", error: "" });

  const [showSubLevelModal, setShowSubLevelModal] = useState(false);
  const [subLevelLabel, setSubLevelLabel] = useState("");
  const [subLevelFirstChoice, setSubLevelFirstChoice] = useState("");
  const [subLevelTarget, setSubLevelTarget] = useState<any>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    nodeId: number;
    level: number;
    parentId: number | string;
  } | null>(null);

  const [openDeleteLabelModal, setOpenDeleteLabelModal] = useState(false);
  const [deleteLabelTarget, setDeleteLabelTarget] = useState<{
    parentId: number | string;
    level: number;
  } | null>(null);

  const fetchedKeysRef = useRef<Set<string>>(new Set());

  const loadLevel = useCallback(
    async (
      parentId: number | string,
      level: number,
      defaultLabel = "",
      options?: { force?: boolean }
    ) => {
      const key = getBucketKey(parentId, level);
      if (!options?.force && fetchedKeysRef.current.has(key)) {
        return;
      }
      fetchedKeysRef.current.add(key);

      setBuckets((prev) =>
        setBucketLoading(
          prev,
          parentId,
          level,
          prev[key]?.label || defaultLabel
        )
      );

      try {
        const result = await fetchLevelOptions({
          fieldId: parentId,
          level,
        }).unwrap();
        const items: LevelItem[] = Array.isArray(result) ? result : [];
        const label =
          level === 1
            ? fieldData.label || defaultLabel
            : items[0]?.field_label || defaultLabel;

        setBuckets((prev) => {
          const previousItems = prev[key]?.items ?? [];
          let next = setBucketData(prev, parentId, level, items, label);

          previousItems.forEach((item) => {
            if (!items.some((updated) => updated.id === item.id)) {
              next = removeNodeDescendantBuckets(next, item.id);
            }
          });

          return next;
        });
      } catch {
        setBuckets((prev) =>
          setBucketData(prev, parentId, level, [], level === 1 ? defaultLabel : "")
        );
      }
    },
    [fetchLevelOptions, fieldData.label]
  );

  const refetchLevel = useCallback(
    (parentId: number | string, level: number, defaultLabel = "") => {
      const key = getBucketKey(parentId, level);
      fetchedKeysRef.current.delete(key);
      return loadLevel(parentId, level, defaultLabel, { force: true });
    },
    [loadLevel]
  );

  useEffect(() => {
    if (!fieldData?.id) {
      return;
    }
    fetchedKeysRef.current = new Set();
    setBuckets({});
    setExpandedLevels([]);
    setExpandedChoices([]);
  }, [fieldData?.id]);

  const toggleLevel = async (
    bucketKey: string,
    parentId: number | string,
    level: number,
    fallbackLabel: string,
    bucket?: LevelBucket
  ) => {
    const isExpanded = expandedLevels.includes(bucketKey);
    if (isExpanded) {
      setExpandedLevels((prev) => prev.filter((item) => item !== bucketKey));
      return;
    }

    setExpandedLevels((prev) => [...prev, bucketKey]);
    if (!bucket?.loaded && !bucket?.loading) {
      await loadLevel(parentId, level, fallbackLabel);
    }
  };

  const toggleChoice = async (
    itemId: number,
    level: number,
    bucket?: LevelBucket
  ) => {
    const isExpanded = expandedChoices.includes(itemId);
    if (isExpanded) {
      setExpandedChoices((prev) => prev.filter((id) => id !== itemId));
      return;
    }

    setExpandedChoices((prev) => [...prev, itemId]);
    if (
      level < MAX_LEVEL &&
      !bucket?.loaded &&
      !bucket?.loading
    ) {
      await loadLevel(itemId, level + 1, "");
    }
  };

  const openAddChoice = (
    parentId: number | string,
    level: number,
    fieldLabel: string
  ) => {
    setShowAddChoiceModal(true);
    setAddChoiceParentId(parentId);
    setAddChoiceLevel(level);
    setAddChoiceFieldLabel(fieldLabel);
    setAddChoiceName("");
    setAddChoiceError("");
  };

  const handleAddChoice = async () => {
    const trimmed = addChoiceName.trim();
    if (!trimmed || addChoiceParentId == null) {
      return;
    }

    const bucketKey = getBucketKey(addChoiceParentId, addChoiceLevel);
    const existingItems = buckets[bucketKey]?.items ?? [];
    if (
      existingItems.some(
        (item) => item.choice?.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      setAddChoiceError("Choice name must be unique");
      return;
    }

    const payload = {
      choice: trimmed,
      sort_order: 1,
      field_type: "multi-level",
      field: addChoiceParentId,
      field_label: addChoiceFieldLabel,
      root_node: fieldData.id,
      level: addChoiceLevel,
    };

    try {
      await addMultiLevelChoice({
        body: payload,
        level: addChoiceLevel,
      }).unwrap();

      await refetchLevel(addChoiceParentId, addChoiceLevel, addChoiceFieldLabel);
      setShowAddChoiceModal(false);
      setAddChoiceName("");
      setAddChoiceError("");
    } catch (error: any) {
      let errorMessage = "Failed to add choice";
      const errorData = error?.data;
      if (errorData && typeof errorData === "object") {
        const errorKeys = Object.keys(errorData);
        if (errorKeys.length && Array.isArray(errorData[errorKeys[0]])) {
          errorMessage = errorData[errorKeys[0]][0];
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      toast.error(errorMessage);
      setAddChoiceError(errorMessage);
    }
  };

  const handleAddSubLevel = async () => {
    if (!subLevelTarget) {
      return;
    }

    const { field, nextLevelNumber } = subLevelTarget;
    const payload = {
      choice: subLevelFirstChoice.trim(),
      sort_order: 1,
      field_type: "multi-level",
      field: field.id,
      field_label: subLevelLabel.trim(),
      root_node: fieldData.id,
      level: nextLevelNumber,
    };

    try {
      await addMultiLevelChoice({
        body: payload,
        level: nextLevelNumber,
      }).unwrap();

      await refetchLevel(field.id, nextLevelNumber, subLevelLabel.trim());
      setExpandedChoices((prev) =>
        prev.includes(field.id) ? prev : [...prev, field.id]
      );
      setExpandedLevels((prev) => {
        const childKey = getBucketKey(field.id, nextLevelNumber);
        return prev.includes(childKey) ? prev : [...prev, childKey];
      });
      setShowSubLevelModal(false);
      setSubLevelLabel("");
      setSubLevelFirstChoice("");
      setSubLevelTarget(null);
    } catch {
      toast.error("Failed to add sub-level");
    }
  };

  const handleDeleteChoice = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      await deleteMultiLevelFields({
        nodeId: deleteTarget.nodeId,
        level: deleteTarget.level,
      }).unwrap();

      const bucketLabel =
        buckets[getBucketKey(deleteTarget.parentId, deleteTarget.level)]?.label ||
        "";

      setBuckets((prev) =>
        removeNodeDescendantBuckets(prev, deleteTarget.nodeId)
      );
      setExpandedChoices((prev) =>
        prev.filter((id) => id !== deleteTarget.nodeId)
      );
      await refetchLevel(
        deleteTarget.parentId,
        deleteTarget.level,
        bucketLabel
      );
      setOpenDeleteModal(false);
      setDeleteTarget(null);
    } catch {
      toast.error("Failed to delete choice");
    }
  };

  const handleDeleteLabel = async () => {
    if (!deleteLabelTarget) {
      return;
    }

    try {
      await deleteMultiLevelFieldsLabel({
        id: String(deleteLabelTarget.parentId),
        level: String(deleteLabelTarget.level),
      }).unwrap();

      const deletedKey = getBucketKey(
        deleteLabelTarget.parentId,
        deleteLabelTarget.level
      );
      setExpandedLevels((prev) => prev.filter((key) => key !== deletedKey));
      await refetchLevel(
        deleteLabelTarget.parentId,
        deleteLabelTarget.level,
        ""
      );
      setOpenDeleteLabelModal(false);
      setDeleteLabelTarget(null);
      toast.success("Level deleted successfully");
    } catch {
      toast.error("Failed to delete level");
    }
  };

  const renderLevel = (
    parentId: number | string,
    level: number,
    fallbackLabel: string
  ) => {
    const bucketKey = getBucketKey(parentId, level);
    const bucket = buckets[bucketKey];
    const label = bucket?.label || fallbackLabel;
    const items = bucket?.items ?? [];
    const isExpanded = expandedLevels.includes(bucketKey);

    return (
      <div
        key={bucketKey}
        style={{
          marginTop: 8,
          padding: "10px 20px",
          background: "#f9fbfd",
          borderRadius: 8,
          boxShadow: "0 2px 8px #0001",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          minHeight: 60,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
            padding: "0 2px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: 700,
              marginBottom: 8,
              fontSize: 17,
              color: "#3a4251",
              padding: "0 2px",
              cursor: "pointer",
              gap: 10,
            }}
            onClick={() =>
              toggleLevel(bucketKey, parentId, level, fallbackLabel, bucket)
            }
          >
            <i
              className={`bi me-2 ${
                isExpanded ? "bi-chevron-down" : "bi-chevron-right"
              }`}
            />
            {label}:
            {level !== 1 && (
              <>
                <button
                  type="button"
                  title="Edit Label"
                  aria-label="Edit Label"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginLeft: 8,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditLabelModalState({
                      show: true,
                      value: label,
                      error: "",
                      parentId,
                      level,
                    });
                  }}
                >
                  <i className="text-dark ms-auto bi bi-pencil fs-4"></i>
                </button>
                <button
                  type="button"
                  title="Delete"
                  aria-label="Delete"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteLabelTarget({ parentId, level });
                    setOpenDeleteLabelModal(true);
                  }}
                >
                  <i className="text-dark bi bi-trash fs-4 mx-1"></i>
                </button>
              </>
            )}
          </div>
          <button
            type="button"
            title="Add Choice"
            aria-label="Add Choice"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
            }}
            onClick={() => openAddChoice(parentId, level, label)}
          >
            <i className="text-dark ms-auto bi bi-plus fs-1"></i>
          </button>
        </div>

        {bucket?.loading && (
          <div style={{ fontSize: 14, color: "#667", marginBottom: 8 }}>
            Loading...
          </div>
        )}

        {isExpanded && (
          <>
            {items.map((item) => {
              const childBucketKey = getBucketKey(item.id, level + 1);
              const childBucket = buckets[childBucketKey];
              const childItems = childBucket?.items ?? [];
              const choiceExpanded = expandedChoices.includes(item.id);

              return (
                <div
                  key={item.id}
                  style={{
                    marginBottom: 12,
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 1px 4px #0001",
                    padding: "16px",
                    border: "1px solid #e3e7ed",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {level < MAX_LEVEL && (
                      <button
                        type="button"
                        title="Expand"
                        aria-label="Expand"
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleChoice(item.id, level, childBucket)}
                      >
                        <i
                          className={`bi ${
                            choiceExpanded ? "bi-chevron-down" : "bi-chevron-right"
                          }`}
                        />
                      </button>
                    )}
                    <input
                      type="text"
                      value={item.choice || ""}
                      disabled
                      style={{
                        marginRight: 8,
                        fontSize: 15,
                        padding: "6px 12px",
                        borderRadius: 4,
                        border: "1px solid #cfd8dc",
                        flex: 1,
                        background: "#f4f7fa",
                        color: "#222",
                        cursor: "not-allowed",
                        opacity: 0.8,
                      }}
                      placeholder={`${label} name`}
                    />
                    <button
                      type="button"
                      title="Edit"
                      aria-label="Edit"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        setEditModalState({
                          show: true,
                          value: item.choice || "",
                          error: "",
                          item,
                          parentId,
                          level,
                        })
                      }
                    >
                      <i className="text-dark ms-auto bi bi-pencil fs-4"></i>
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setDeleteTarget({
                          nodeId: item.id,
                          level,
                          parentId,
                        });
                        setOpenDeleteModal(true);
                      }}
                    >
                      <i className="text-dark bi bi-trash fs-4 mx-1"></i>
                    </button>
                    {level < MAX_LEVEL && (
                      <button
                        type="button"
                        title="Add Sub-Level"
                        aria-label="Add Sub-Level"
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setSubLevelTarget({
                            field: item,
                            parentLabel: label,
                            parentChoice: item.choice,
                            nextLevelNumber: level + 1,
                          });
                          setSubLevelLabel("");
                          setSubLevelFirstChoice("");
                          setShowSubLevelModal(true);
                        }}
                      >
                        <i className="fas bi-plus cursor-pointer fs-1 text-dark mx-1"></i>
                      </button>
                    )}
                  </div>

                  {childBucket?.loading && choiceExpanded && (
                    <div style={{ fontSize: 14, color: "#667", marginTop: 8 }}>
                      Loading...
                    </div>
                  )}

                  {choiceExpanded &&
                    childItems.length > 0 &&
                    level < MAX_LEVEL &&
                    renderLevel(
                      item.id,
                      level + 1,
                      childBucket?.label || ""
                    )}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const bucketKeyForAdd =
    addChoiceParentId != null
      ? getBucketKey(addChoiceParentId, addChoiceLevel)
      : "";
  const addChoiceItems = bucketKeyForAdd ? buckets[bucketKeyForAdd]?.items ?? [] : [];

  return (
    <div>
      <EditLabelModal
        show={editLabelModalState.show}
        value={editLabelModalState.value}
        error={editLabelModalState.error}
        isLoading={isUpdatingLabel}
        labelName={editLabelModalState.value}
        onChange={(val) =>
          setEditLabelModalState((state) => ({ ...state, value: val, error: "" }))
        }
        onClose={() =>
          setEditLabelModalState((state) => ({ ...state, show: false, error: "" }))
        }
        onSubmit={async () => {
          if (
            editLabelModalState.parentId == null ||
            editLabelModalState.level == null
          ) {
            return;
          }

          try {
            await updateLabelMultiLevelDynamic({
              rootNode: fieldData.id,
              level: editLabelModalState.level,
              body: { field_label: editLabelModalState.value },
            }).unwrap();

            await refetchLevel(
              editLabelModalState.parentId as number | string,
              editLabelModalState.level as number,
              editLabelModalState.value.trim()
            );
            setEditLabelModalState((state) => ({ ...state, show: false, error: "" }));
          } catch (error: any) {
            setEditLabelModalState((state) => ({
              ...state,
              error: error?.data?.detail || "Failed to update label",
            }));
          }
        }}
        disableSave={!editLabelModalState.value.trim()}
      />

      <EditChoiceModal
        show={editModalState.show}
        value={editModalState.value}
        error={editModalState.error}
        isLoading={isUpdating}
        onChange={(val) =>
          setEditModalState((state) => ({ ...state, value: val, error: "" }))
        }
        onClose={() =>
          setEditModalState((state) => ({ ...state, show: false, error: "" }))
        }
        onSubmit={async () => {
          if (
            !editModalState.item ||
            editModalState.parentId == null ||
            editModalState.level == null
          ) {
            return;
          }

          try {
            await updateMultiLevelDynamic({
              nodeId: editModalState.item.id,
              level: editModalState.level,
              body: {
                ...editModalState.item,
                choice: editModalState.value.trim(),
              },
            }).unwrap();

            const bucketLabel =
              buckets[
                getBucketKey(
                  editModalState.parentId as number | string,
                  editModalState.level as number
                )
              ]?.label || "";
            await refetchLevel(
              editModalState.parentId as number | string,
              editModalState.level as number,
              bucketLabel
            );
            setEditModalState((state) => ({ ...state, show: false, error: "" }));
          } catch (error: any) {
            setEditModalState((state) => ({
              ...state,
              error: error?.data?.detail || "Failed to update choice",
            }));
          }
        }}
        disableSave={!editModalState.value.trim()}
      />

      <AddSubLabelModal
        show={showSubLevelModal}
        value={subLevelLabel}
        error=""
        isLoading={isAdding}
        onChange={setSubLevelLabel}
        onClose={() => {
          setShowSubLevelModal(false);
          setSubLevelLabel("");
          setSubLevelFirstChoice("");
          setSubLevelTarget(null);
        }}
        onSubmit={handleAddSubLevel}
        disableAdd={!subLevelLabel.trim() || !subLevelFirstChoice.trim()}
        label={`Add Sub Label for ${subLevelTarget?.parentLabel || ""}`}
        parentChoice={subLevelTarget?.parentChoice || ""}
        subLevelFirstChoice={subLevelFirstChoice}
        onChangeSubLevelFirstChoice={setSubLevelFirstChoice}
      />

      <AddChoiceModal
        show={showAddChoiceModal}
        value={addChoiceName}
        error={addChoiceError}
        isLoading={isAdding}
        onChange={(val) => {
          setAddChoiceName(val);
          setAddChoiceError("");
        }}
        onClose={() => {
          setShowAddChoiceModal(false);
          setAddChoiceName("");
          setAddChoiceError("");
        }}
        onSubmit={handleAddChoice}
        disableAdd={
          !addChoiceName.trim() ||
          addChoiceItems.some(
            (item) =>
              item.choice?.toLowerCase() === addChoiceName.trim().toLowerCase()
          )
        }
      />

      {renderLevel(fieldData.id, 1, fieldData.label || "Choices")}

      <ReactModal
        isOpen={openDeleteModal}
        onRequestClose={() => setOpenDeleteModal(false)}
        style={deleteCustomModalStyles}
        shouldCloseOnOverlayClick={false}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "center",
          }}
        >
          <div style={{ color: "#3071e4", fontSize: 16 }}>Are you sure?</div>
          <div style={{ marginBottom: 15 }}>
            Do you really want to delete this choice? This process cannot be undone.
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="btn btn-danger" onClick={handleDeleteChoice}>
              Delete
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setOpenDeleteModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={openDeleteLabelModal}
        onRequestClose={() => setOpenDeleteLabelModal(false)}
        style={deleteCustomModalStyles}
        shouldCloseOnOverlayClick={false}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "center",
          }}
        >
          <div style={{ color: "#3071e4", fontSize: 16 }}>Are you sure?</div>
          <div style={{ marginBottom: 15 }}>
            Do you really want to delete this level? This process cannot be undone.
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="btn btn-danger" onClick={handleDeleteLabel}>
              Delete
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setOpenDeleteLabelModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </ReactModal>
    </div>
  );
};

export default LazyNestedFieldConfigurator;